import React, { useCallback, useEffect, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import EquipmentHoldingProfileForm from '../../EquipmentHoldingProfile/EquipmentHoldingProfileForm';
import EquipmentHoldingProfileList from '../../EquipmentHoldingProfile/EquipmentHoldingProfileList';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import RBIDropdownSelector from '../../ProfilesTabs/RBIDropdownSelector';
import { getEntities as getModels } from '../../../redux/actions/modelsActions';
import { selectModels } from '../../../redux/selectors/modelsSelector';
import {
  getCountries as getCountriesByRegion,
  getRegions,
  getStates,
} from '../../../redux/actions/regionsActions';
import countriesTreater from '../../ProfilesTabs/helpers/countriesTreater';
import RBIAutocompleteSearch from '../../RBIAutocompleteSearch/RBIAutocompleteSearch';
import { VIEW_MODE_STATES } from '../../EquipmentHoldingProfile/utils';
import { getEquipmentHoldingProfiles } from '../../../redux/actions/equipmentsActions';
import {
  cleanDefaultString,
  wrapEquipmentAvailabilities,
} from '../../../utils/helpers';
import { useTranslation } from 'react-i18next';
import styles from '../styles/styles';

function HoldingProfilesTab() {
  const classes = styles();
  const { t } = useTranslation();
  const dispatch: any = useDispatch();
  const RAM_DEFAULT_VALUES = {
    EQUIPMENT_HOLDING_PROFILE: {
      DEFAULT_AUTOCOMPLETE_VALUE: undefined,
      DEFAULT_ALL_FILTER_DISPLAY: 'ALL',
      DEFAULT_ALL_FILTER_VALUE: 'all',
      DEFAULT_GLOBAL_FILTER_DISPLAY: 'GLOBAL',
      DEFAULT_GLOBAL_FILTER_VALUE: 'GLOBAL',
    },
  };
  const [DefaultFilterDisplay, DefaultFilterValue] = [
    RAM_DEFAULT_VALUES.EQUIPMENT_HOLDING_PROFILE.DEFAULT_ALL_FILTER_DISPLAY,
    RAM_DEFAULT_VALUES.EQUIPMENT_HOLDING_PROFILE.DEFAULT_ALL_FILTER_VALUE,
  ];
  const DefaultAutocompleteValue = undefined;
  const [viewMode, setViewMode] = useState(VIEW_MODE_STATES.SEARCH);
  const [selectedFilters, setSelectedFilters] = useState({
    manufacturer: DefaultAutocompleteValue,
    model: DefaultAutocompleteValue,
    region: 'GLOBAL',
    state: DefaultFilterValue,
    country: DefaultFilterValue,
    category: DefaultAutocompleteValue,
  });

  const sortAlphabeticByName = array =>
    array && Array.isArray(array)
      ? array.sort((optA, optB) =>
          optA.name > optB.name ? 1 : optA < optB ? -1 : 0,
        )
      : [];

  const {
    manufacturersCatalog,
    selectedBrand,
    categoriesCatalog,
    modelsCatalog,
    regions,
    countries,
    states,
  } = useSelector((state: RootStateOrAny) => ({
    manufacturersCatalog: sortAlphabeticByName(
      state.ManufacturersReducer.entitiesCatalog,
    ),
    categoriesCatalog: sortAlphabeticByName(
      state.CategoriesReducer.entitiesCatalog,
    ),
    modelsCatalog: sortAlphabeticByName(selectModels(state)),
    regions: state.RegionsReducer.regions,
    countries: state.RegionsReducer.countries,
    states: state.RegionsReducer.states,
    selectedBrand: state.UIReducer.selectedBrand,
  }));
  const [autocompleteSelectors, setAutocompleteSelectors] = useState({
    manufacturer: '',
    model: '',
    category: '',
  });
  const [copyMessage, setCopyMessage] = useState(false);
  const onCancelForm = () => {
    setCopyMessage(false);
    setAutocompleteSelectors({
      manufacturer: '',
      model: '',
      category: '',
    });
    setSelectedFilters({
      manufacturer: DefaultAutocompleteValue,
      model: DefaultAutocompleteValue,
      region: 'GLOBAL',
      state: DefaultFilterValue,
      country: DefaultFilterValue,
      category: DefaultAutocompleteValue,
    });
  };
  const handleClickSearchButton = () => {
    setViewMode(VIEW_MODE_STATES.LIST);
  };
  const searchHoldingProfiles = () => {
    dispatch(
      getEquipmentHoldingProfiles({
        brand: selectedBrand,
        page: 1,
        limit: 10,
        ...wrapEquipmentAvailabilities(
          cleanDefaultString(selectedFilters, 'all'),
        ),
      }),
    );
  };
  useEffect(() => {
    if (viewMode === VIEW_MODE_STATES.LIST) {
      searchHoldingProfiles();
    }
  }, [viewMode]);
  useEffect(() => {
    if (selectedFilters.region !== 'GLOBAL') {
      dispatch(getCountriesByRegion(selectedFilters.region));
    }
    setSelectedFilters(prevState => ({
      ...prevState,
      country: DefaultFilterValue,
    }));
  }, [dispatch, selectedFilters.region]);

  useEffect(() => {
    if (selectedBrand) {
      dispatch(getModels({ brand: selectedBrand }));
      dispatch(getRegions());
    }
  }, [dispatch, selectedBrand]);
  useEffect(() => {
    if (selectedFilters.country !== DefaultFilterValue) {
      dispatch(getStates(selectedFilters.region, selectedFilters.country));
    }
    setSelectedFilters(prevState => ({
      ...prevState,
      state: DefaultFilterValue,
    }));
  }, [dispatch, selectedFilters.country, selectedFilters.region]);

  const handleChangeSelectFilter = filterKey => selected => {
    setSelectedFilters(prevState => ({ ...prevState, [filterKey]: selected }));
  };

  const handleChangeAutocomplete = filterKey => (name: string, value: any) => {
    setSelectedFilters(prevState => ({ ...prevState, [filterKey]: value?.id }));
    setAutocompleteSelectors(prevState => ({
      ...prevState,
      [filterKey]: value,
    }));
  };

  //Debounce part for on demand catalogs
  const [itemInput, setItemInput] = useState<string>('');
  const [timer, setTimer] = useState<any>(null);
  const callBackFunctionItems = useCallback(
    (filterKey, value) => {
      clearTimeout(timer);
      if ((itemInput !== value && value.length) || value === '') {
        setItemInput(value);
        setTimer(
          setTimeout(() => {
            if (filterKey === 'model') {
              dispatch(
                getModels({ brand: selectedBrand, name: value || null }),
              );
            }
          }, 600),
        );
      }
    },
    [timer, setItemInput, setTimer],
  );

  const handleChangeInput = filterKey => (name: string, value: any) => {
    callBackFunctionItems(filterKey, value);
  };

  return (
    <>
      {copyMessage ? (
        <Typography
          variant="body1"
          align="center"
          paragraph
          className={classes.copyMessage}
        >
          {t('app.common.messages.profileAlreadyExistChangeAnyValue')}
        </Typography>
      ) : (
        ''
      )}
      <Grid container>
        <Grid item sm={4}>
          <Box p={1}>
            <RBIAutocompleteSearch
              onChange={handleChangeAutocomplete('category')}
              disabled={
                viewMode !== VIEW_MODE_STATES.LIST &&
                viewMode !== VIEW_MODE_STATES.CLONE &&
                viewMode !== VIEW_MODE_STATES.SEARCH
              }
              value={autocompleteSelectors.category}
              id={'Category'}
              label={t('equipment.category')}
              width={'100%'}
              options={categoriesCatalog.filter(c => c.is_active)}
            />
          </Box>
        </Grid>
        <Grid item sm={4}>
          <Box p={1}>
            <RBIAutocompleteSearch
              onChange={handleChangeAutocomplete('manufacturer')}
              disabled={
                viewMode !== VIEW_MODE_STATES.LIST &&
                viewMode !== VIEW_MODE_STATES.CLONE &&
                viewMode !== VIEW_MODE_STATES.SEARCH
              }
              value={autocompleteSelectors.manufacturer}
              id={'Manufacturer'}
              label={t('equipment.manufacturer')}
              width={'100%'}
              options={manufacturersCatalog.filter(m => m.status)}
            />
          </Box>
        </Grid>
        <Grid item sm={4}>
          <Box p={1}>
            <RBIAutocompleteSearch
              onChange={handleChangeAutocomplete('model')}
              onChangeInput={handleChangeInput('model')}
              value={autocompleteSelectors.model}
              disabled={
                viewMode !== VIEW_MODE_STATES.LIST &&
                viewMode !== VIEW_MODE_STATES.CLONE &&
                viewMode !== VIEW_MODE_STATES.SEARCH
              }
              id={'Model'}
              label={t('equipment.model')}
              width={'100%'}
              options={modelsCatalog}
            />
          </Box>
        </Grid>
        <Grid item sm={4}>
          <RBIDropdownSelector
            name={t('app.common.region')}
            options={regions}
            selected={selectedFilters.region}
            extraOption={'GLOBAL'}
            extraOptionValue={'GLOBAL'}
            isDisabled={
              viewMode !== VIEW_MODE_STATES.EDIT &&
              viewMode !== VIEW_MODE_STATES.CLONE &&
              viewMode !== VIEW_MODE_STATES.CREATE
            }
            handleChangeSelect={handleChangeSelectFilter('region')}
          />
        </Grid>
        <Grid item sm={4}>
          <RBIDropdownSelector
            name={t('app.common.country')}
            options={countriesTreater(countries)}
            selected={selectedFilters.country}
            extraOption={DefaultFilterDisplay}
            extraOptionValue={DefaultFilterValue}
            handleChangeSelect={handleChangeSelectFilter('country')}
            isDisabled={
              selectedFilters.region === 'GLOBAL' ||
              (viewMode !== VIEW_MODE_STATES.EDIT &&
                viewMode !== VIEW_MODE_STATES.CLONE &&
                viewMode !== VIEW_MODE_STATES.CREATE)
            }
          />
        </Grid>
        <Grid item sm={4}>
          <RBIDropdownSelector
            name={t('app.common.state')}
            options={states.map(state => ({
              id: state.id,
              name: state.description,
            }))}
            selected={selectedFilters.state}
            extraOption={DefaultFilterDisplay}
            extraOptionValue={DefaultFilterValue}
            handleChangeSelect={handleChangeSelectFilter('state')}
            isDisabled={
              selectedFilters.region === 'GLOBAL' ||
              (viewMode !== VIEW_MODE_STATES.EDIT &&
                viewMode !== VIEW_MODE_STATES.CLONE &&
                viewMode !== VIEW_MODE_STATES.CREATE)
            }
          />
        </Grid>
        {viewMode === VIEW_MODE_STATES.SEARCH && (
          <Grid item>
            <Button
              variant="contained"
              color="default"
              style={{ margin: 0 }}
              disabled={
                !selectedFilters.model &&
                !selectedFilters.manufacturer &&
                !selectedFilters.category
              }
              onClick={handleClickSearchButton}
            >
              Search
            </Button>
          </Grid>
        )}
      </Grid>
      {viewMode === VIEW_MODE_STATES.LIST && (
        <EquipmentHoldingProfileList
          searchFilters={selectedFilters}
          setViewMode={setViewMode}
          setAutocomleteSelectors={setAutocompleteSelectors}
          setCopyMessage={setCopyMessage}
        />
      )}
      {(viewMode === VIEW_MODE_STATES.CREATE ||
        viewMode === VIEW_MODE_STATES.EDIT ||
        viewMode === VIEW_MODE_STATES.CLONE) && (
        <EquipmentHoldingProfileForm
          handleCancel={onCancelForm}
          searchFilters={selectedFilters}
          autocompleteSelectors={autocompleteSelectors}
          viewMode={viewMode}
          setViewMode={setViewMode}
          onClose={() =>
            setSelectedFilters({
              ...selectedFilters,
              region: 'GLOBAL',
              state: DefaultFilterValue,
              country: DefaultFilterValue,
            })
          }
          setCopyMessage={setCopyMessage}
        />
      )}
    </>
  );
}

export default HoldingProfilesTab;
